import * as Icons from "@ant-design/icons";
import { LeftOutlined, RightOutlined, SearchOutlined, UpOutlined, DownOutlined, CodeOutlined } from "@ant-design/icons";
import { Button, Col, Menu, Row, Tooltip, Input, Switch, Tag } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import DynamicDrawer from "../../components/Settings/DeveloperTools/Tabs/modals/DynamicDrawer";
import { useMenu } from "../../store/context/admin/menu-context";
import { useTenant } from "../../store/context/tenant-context";
import { useTheme } from "../../store/context/theme-context";
import { usePermissions } from "../../store/context/permission-context";
import { getSessionData } from "../../utils/interceptors/localStorageService";
import { PERMISSION_CODES } from "../../shared/constants/constants";
// Removed feature gating here; filtering now handled in MenuContext
import "./MainLayout.css";

const { SubMenu } = Menu;

const MainLayout = ({ collapsed, setCollapsed, type }) => {
  const [openKeys, setOpenKeys] = useState([]);
  const [selectedMenuGroup, setSelectedMenuGroup] = useState(null);
  const [menuData, setMenuData] = useState([]);
  const [isHovered, setIsHovered] = useState(false);
  const [selectedCode, setSelectedCode] = useState("");
  const [moduleSearchQuery, setModuleSearchQuery] = useState("");
  const [menuSearchQuery, setMenuSearchQuery] = useState("");
  const [isModuleSectionCollapsed, setIsModuleSectionCollapsed] = useState(false);
  const [isDeveloperMode, setIsDeveloperMode] = useState(false);

  // Drawer state
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerConfig, setDrawerConfig] = useState({
    configCmd: "GetConfigQuery",
    configParams: {
      path: "Webhooks",
      configName: "MenuDrawerConfig",
    },
    width: "50%",
    placement: "right",
  });

  const { theme } = useTheme();
  const tenant = useTenant();
  const { hasPermission } = usePermissions();

  const location = useLocation();
  const { menuList } = useMenu();
  const [sessionData, setSessionData] = useState(null);

  // Check if user has developer tools permission
  const isDeveloperToolsManager = hasPermission(
    PERMISSION_CODES.BPM_MANAGEMENT.DEVELOPER_TOOLS_MANAGER
  );

  console.log('Developer Mode Debug:', {
    collapsed,
    isDeveloperToolsManager,
    isDeveloperMode,
    permissionCode: PERMISSION_CODES.BPM_MANAGEMENT.DEVELOPER_TOOLS_MANAGER
  });

  // Developer menu items grouped by category
  const developerMenuGroups = [
    {
      category: 'UI',
      icon: 'LayoutOutlined',
      color: '#1890ff',
      items: [
        { 
          key: 'forms', 
          label: 'Forms', 
          icon: 'FormOutlined',
          path: '/forms',
          description: 'Form builder'
        },
        { 
          key: 'views', 
          label: 'Views', 
          icon: 'EyeOutlined',
          path: '/views',
          description: 'Custom data views'
        },
        { 
          key: 'tables', 
          label: 'Tables', 
          icon: 'TableOutlined',
          path: '/rule-table',
          description: 'Database table schemas'
        },
      ]
    },
    {
      category: 'Logic',
      icon: 'FunctionOutlined',
      color: '#52c41a',
      items: [
        { 
          key: 'business-rules', 
          label: 'Business Rules', 
          icon: 'BranchesOutlined',
          path: '/business-rules',
          description: 'Define business logic'
        },
        { 
          key: 'process', 
          label: 'Process', 
          icon: 'NodeIndexOutlined',
          path: '/Process',
          description: 'Business process flows'
        },
        { 
          key: 'command-chains', 
          label: 'Command Chains', 
          icon: 'LinkOutlined',
          path: '/chains',
          description: 'Chain multiple commands'
        },
        { 
          key: 'webhooks', 
          label: 'Webhooks', 
          icon: 'ApiOutlined',
          path: '/webhooks',
          description: 'Manage webhook endpoints'
        },
        { 
          key: 'recurring-jobs', 
          label: 'Recurring Jobs', 
          icon: 'ClockCircleOutlined',
          path: '/recurring-jobs',
          description: 'Schedule and manage jobs'
        },
        { 
          key: 'actuarial-sheets', 
          label: 'Actuarial Sheets', 
          icon: 'CalculatorOutlined',
          path: '/acturial-sheets',
          description: 'Insurance calculations'
        },
      ]
    },
    {
      category: 'Templates',
      icon: 'FileTextOutlined',
      color: '#faad14',
      items: [
        { 
          key: 'html-templates', 
          label: 'HTML Templates', 
          icon: 'Html5Outlined',
          path: '/html-templates',
          description: 'Create HTML templates'
        },
        { 
          key: 'document-templates', 
          label: 'Document Templates', 
          icon: 'FileTextOutlined',
          path: '/document-template',
          description: 'Manage document templates'
        },
      ]
    },
    {
      category: 'Configuration',
      icon: 'SettingOutlined',
      color: '#722ed1',
      items: [
        { 
          key: 'object-definitions', 
          label: 'Object Definitions', 
          icon: 'DatabaseOutlined',
          path: '/object-definitions',
          description: 'Define data objects'
        },
        { 
          key: 'secure-credentials', 
          label: 'Secure Credentials', 
          icon: 'LockOutlined',
          path: '/secure-credentials',
          description: 'Store secure credentials'
        },
      ]
    },
  ];

  useEffect(() => {
    const storedSessionData = getSessionData();
    if (storedSessionData) {
      setSessionData(storedSessionData);
      // const firstNameInitial = (storedSessionData?.firstName ?? "")
      //   .charAt(0)
      //   .toUpperCase();
      // const lastNameInitial = (storedSessionData?.lastName ?? "")
      //   .charAt(0)
      //   .toUpperCase();
      // setInitials(`${firstNameInitial}${lastNameInitial}`);
    }
  }, []);

  const findMenuGroupByPath = useCallback(
    (currentPath) => {
      if (!menuList?.menus || menuList.menus.length === 0) return null;

      for (const menu of menuList.menus) {
        if (menu.path === currentPath) {
          return menu.menu_groups?.[0] || null;
        }

        if (menu.submenus && Array.isArray(menu.submenus)) {
          for (const submenu of menu.submenus) {
            if (submenu.path === currentPath) {
              return menu.menu_groups?.[0] || null;
            }
          }
        }
      }

      for (const menu of menuList.menus) {
        if (menu.path && currentPath.startsWith(menu.path)) {
          return menu.menu_groups?.[0] || null;
        }

        if (menu.submenus && Array.isArray(menu.submenus)) {
          for (const submenu of menu.submenus) {
            if (submenu.path && currentPath.startsWith(submenu.path)) {
              return menu.menu_groups?.[0] || null;
            }
          }
        }
      }

      return null;
    },
    [menuList]
  );

  const findSelectedCodeByLocation = useCallback(
    (currentPath) => {
      if (!menuList?.menus || menuList.menus.length === 0) return "";

      for (const menu of menuList.menus) {
        if (menu.path === currentPath) {
          return menu.code;
        }

        if (menu.submenus && Array.isArray(menu.submenus)) {
          for (const submenu of menu.submenus) {
            if (submenu.path === currentPath) {
              return submenu.code;
            }
          }
        }
      }

      for (const menu of menuList.menus) {
        if (menu.path && currentPath.startsWith(menu.path)) {
          return menu.code;
        }

        if (menu.submenus && Array.isArray(menu.submenus)) {
          for (const submenu of menu.submenus) {
            if (submenu.path && currentPath.startsWith(submenu.path)) {
              return submenu.code;
            }
          }
        }
      }

      return "";
    },
    [menuList]
  );

  useEffect(() => {
    // Don't fetch menu schema here - AdminLayout handles it
    // This component is only responsible for rendering the menu once it's loaded
    if (!menuList) {
      return;
    }

    const currentPath = location.pathname;
    const menuGroupFromPath = findMenuGroupByPath(currentPath);
    const codeFromLocation = findSelectedCodeByLocation(currentPath);

    // Update selectedCode if we found a valid code
    if (codeFromLocation) {
      setSelectedCode(codeFromLocation);
    }

    // Only derive selectedMenuGroup from URL when not explicitly chosen yet
    if (!selectedMenuGroup) {
      if (menuGroupFromPath) {
        console.log("Initializing menu group from path:", menuGroupFromPath);
        setSelectedMenuGroup(menuGroupFromPath);
      } else if (menuList?.modules?.length > 0) {
        console.log("Setting default menu group:", menuList.modules[0].code);
        setSelectedMenuGroup(menuList.modules[0].code);
      }
    }
    // If a group is already selected (e.g., user clicked a module), do not override it here
  }, [
    menuList,
    location.pathname,
    findMenuGroupByPath,
    findSelectedCodeByLocation,
    selectedMenuGroup,
  ]);

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const { t } = useTranslation();

  const retrieveMenuData = useCallback(() => {
    // If no menu group is selected, don't show any menus
    if (!selectedMenuGroup) {
      setMenuData([]);
      return;
    }

    var returnData = (menuList?.menus ?? []).filter(
      (item) => item.menu_groups && item.menu_groups.includes(selectedMenuGroup)
    );

    setMenuData(returnData);
  }, [menuList, selectedMenuGroup]);

  useEffect(() => {
    // Only retrieve menu data if we have a valid menu group and menu list
    if (selectedMenuGroup && menuList?.menus) {
      retrieveMenuData();
    }
  }, [selectedMenuGroup, menuList, retrieveMenuData]);

  const navigate = useNavigate();

  // Modified handleMenuItemClick to handle drawer type
  const handleMenuItemClick = (path, code, submenu = null) => {

    // Check if submenu type is drawer
    if (submenu && submenu.type === "drawer") {
      // Set drawer configuration from submenu

      console.log("submenu.formFieldsColumn?? ", submenu.formFieldsColumn);
      setDrawerConfig({
        configCmd: submenu.configCmd || "GetConfigQuery",
        formFieldsColumn: parseInt(submenu.formFieldsColumn) || 2,
        configParams: submenu.configParams || {
          path: submenu.path || "Webhooks",
          configName: submenu.configName || "MenuDrawerConfig",
        },
        width: submenu.width || "50%",
        placement: submenu.placement || "right",
      });
      setDrawerVisible(true);
      return;
    }

    // Normal navigation for non-drawer items
    if (path) {
      navigate(path);
    }
    if (code) {
      setSelectedCode(code);
    }
  };

  const toggleMenuClick = (menuKey) => {
    console.log("menuKey?? ", menuKey, openKeys);
    if (isHovered) {
      setIsHovered(false);
      setOpenKeys([]);
    } else {
      setIsHovered(true);
      setOpenKeys([menuKey]);
    }
  };

  const handleMenuGroupSelect = (group) => {
    console.log("Selecting menu group:", group, "Current:", selectedMenuGroup);
    if (group !== selectedMenuGroup) {
      setSelectedMenuGroup(group);
      setMenuSearchQuery(""); // Clear menu search when switching groups
      if (collapsed) setCollapsed(false);
    }
  };

  // Filter modules based on search query
  const filteredModules = (menuList?.modules ?? []).filter((group) => {
    if (group.code.includes("divider")) return false;
    if (!moduleSearchQuery.trim()) return true;
    
    const searchLower = moduleSearchQuery.toLowerCase();
    const name = t(group?.translationCode || group?.name, {
      defaultValue: group?.name,
    }).toLowerCase();
    
    return name.includes(searchLower);
  });

  // Filter menu items based on search query
  const filteredMenuData = menuData.filter((item) => {
    if (!menuSearchQuery.trim()) return true;
    
    const searchLower = menuSearchQuery.toLowerCase();
    const itemLabel = t(item?.translationCode || item?.label, {
      defaultValue: item?.label,
    }).toLowerCase();
    
    // Check if item label matches
    if (itemLabel.includes(searchLower)) return true;
    
    // Check if any submenu matches
    if (item.submenus) {
      return item.submenus.some((subItem) => {
        const subLabel = t(subItem?.translationCode || subItem?.label, {
          defaultValue: subItem?.label,
        }).toLowerCase();
        return subLabel.includes(searchLower);
      });
    }
    
    return false;
  });

  // no-op: drawer close handled inline

  // if (isFetchingMenuSchema) return <> </>;

  // Don't render if no menu group is selected or no menu data
  if (!selectedMenuGroup || !menuList?.modules || !menuList?.menus) {
    console.log("Not rendering - missing data:", {
      selectedMenuGroup,
      hasModules: !!menuList?.modules,
      hasMenus: !!menuList?.menus,
    });
    // return <> </>;
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: "calc(100vh - 48px)",
          backgroundColor: theme === "dark" ? "#141414" : "#ffffff",
          borderRight: `1px solid ${theme === "dark" ? "#303030" : "#f0f0f0"}`,
        }}
      >
        {/* Header Section with User Info */}
        <div
          style={{
            padding: collapsed ? '12px 8px' : '16px',
            borderBottom: `1px solid ${theme === "dark" ? "#303030" : "#f0f0f0"}`,
            backgroundColor: theme === "dark" ? "#1f1f1f" : "#fafafa",
          }}
        >
          {!collapsed ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {sessionData && (
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    backgroundColor: tenant.theme.colorPrimary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '16px',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {sessionData?.firstName?.charAt(0)}{sessionData?.lastName?.charAt(0)}
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ 
                  fontSize: '14px', 
                  fontWeight: 600, 
                  color: theme === "dark" ? "#ffffff" : "#262626",
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {sessionData?.displayName}
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  color: theme === "dark" ? "#8c8c8c" : "#8c8c8c",
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {sessionData?.userClassificationDescription}
                </div>
              </div>
              <Button
                type="text"
                size="small"
                icon={<LeftOutlined style={{ fontSize: '12px' }} />}
                onClick={toggleCollapsed}
                style={{ flexShrink: 0 }}
              />
            </div>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <Button
                type="text"
                size="small"
                icon={<RightOutlined />}
                onClick={toggleCollapsed}
              />
            </div>
          )}
        </div>

        {/* Developer Mode Toggle */}
        {!collapsed && isDeveloperToolsManager && (
          <div
            style={{
              padding: '12px 16px',
              borderBottom: `1px solid ${theme === "dark" ? "#303030" : "#f0f0f0"}`,
              backgroundColor: isDeveloperMode 
                ? `${tenant.theme.colorPrimary}10` 
                : theme === "dark" ? "#1f1f1f" : "#fafafa",
              transition: 'all 0.3s ease',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CodeOutlined 
                  style={{ 
                    fontSize: '16px',
                    color: isDeveloperMode ? tenant.theme.colorPrimary : theme === "dark" ? "#8c8c8c" : "#8c8c8c"
                  }} 
                />
                <span style={{ 
                  fontSize: '13px', 
                  fontWeight: 600,
                  color: isDeveloperMode ? tenant.theme.colorPrimary : theme === "dark" ? "#d9d9d9" : "#262626"
                }}>
                  Developer Mode
                </span>
                {isDeveloperMode && (
                  <Tag color={tenant.theme.colorPrimary} style={{ margin: 0, fontSize: '10px', padding: '0 6px' }}>
                    ACTIVE
                  </Tag>
                )}
              </div>
              <Switch
                size="small"
                checked={isDeveloperMode}
                onChange={(checked) => {
                  setIsDeveloperMode(checked);
                  console.log('Developer mode toggled:', checked);
                  if (checked) {
                    setIsModuleSectionCollapsed(true); // Collapse regular modules when entering dev mode
                  }
                }}
                style={{
                  backgroundColor: isDeveloperMode ? tenant.theme.colorPrimary : undefined
                }}
              />
            </div>
            <div style={{ 
              fontSize: '11px',
              color: theme === "dark" ? "#8c8c8c" : "#8c8c8c",
              fontStyle: 'italic',
              paddingLeft: '24px'
            }}>
              {isDeveloperMode ? 'Developer tools and configurations' : 'Toggle to access developer tools'}
            </div>
          </div>
        )}

        {/* Module Groups Section */}
        {!collapsed && !isDeveloperMode && (
          <div style={{
            borderBottom: `1px solid ${theme === "dark" ? "#303030" : "#f0f0f0"}`,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: isModuleSectionCollapsed ? 'auto' : '30vh',
            transition: 'max-height 0.3s ease',
          }}>
            <div style={{ 
              padding: '12px 12px 8px 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              userSelect: 'none',
            }}
            onClick={() => setIsModuleSectionCollapsed(!isModuleSectionCollapsed)}
            >
              <div style={{
                fontSize: '11px',
                fontWeight: 600,
                color: theme === "dark" ? "#8c8c8c" : "#8c8c8c",
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                Modules {filteredModules.length > 0 && `(${filteredModules.length})`}
                {isModuleSectionCollapsed ? (
                  <DownOutlined style={{ fontSize: '10px' }} />
                ) : (
                  <UpOutlined style={{ fontSize: '10px' }} />
                )}
              </div>
              
              <Tooltip title={isModuleSectionCollapsed ? "Expand modules" : "Collapse modules"}>
                <Button
                  type="text"
                  size="small"
                  icon={isModuleSectionCollapsed ? <DownOutlined /> : <UpOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsModuleSectionCollapsed(!isModuleSectionCollapsed);
                  }}
                  style={{
                    fontSize: '12px',
                    color: theme === "dark" ? "#8c8c8c" : "#8c8c8c",
                  }}
                />
              </Tooltip>
            </div>
            
            {!isModuleSectionCollapsed && (
              <>
                <div style={{ padding: '0 12px 8px 12px' }}>
                  {/* Module Search */}
                  <Input
                    placeholder="Search modules..."
                    prefix={<SearchOutlined style={{ color: theme === "dark" ? "#8c8c8c" : "#bfbfbf" }} />}
                    value={moduleSearchQuery}
                    onChange={(e) => setModuleSearchQuery(e.target.value)}
                    allowClear
                    style={{
                      fontSize: '13px',
                      borderRadius: '6px',
                    }}
                  />
                </div>
                
                <div style={{
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  padding: '8px 12px 12px 12px',
                  flex: 1,
                }}>
                  {filteredModules.length > 0 ? (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))',
                      gap: '8px',
                    }}>
                      {filteredModules.map((group) => {
                        const IconComponent = Icons[group.icon];
                        const isSelected = selectedMenuGroup === group.code;

                        return (
                          <div
                            key={group.code}
                            onClick={() => handleMenuGroupSelect(group.code)}
                            style={{
                              padding: '12px 8px',
                              borderRadius: '8px',
                              border: `1px solid ${isSelected 
                                ? tenant.theme.colorPrimary 
                                : theme === "dark" ? "#303030" : "#f0f0f0"}`,
                              backgroundColor: isSelected 
                                ? `${tenant.theme.colorPrimary}15` 
                                : theme === "dark" ? "#1f1f1f" : "#ffffff",
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '6px',
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.borderColor = tenant.theme.colorPrimary + '80';
                                e.currentTarget.style.backgroundColor = `${tenant.theme.colorPrimary}08`;
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.borderColor = theme === "dark" ? "#303030" : "#f0f0f0";
                                e.currentTarget.style.backgroundColor = theme === "dark" ? "#1f1f1f" : "#ffffff";
                              }
                            }}
                          >
                            {IconComponent && (
                              <IconComponent 
                                style={{ 
                                  fontSize: '18px',
                                  color: isSelected ? tenant.theme.colorPrimary : theme === "dark" ? "#bfbfbf" : "#595959"
                                }} 
                              />
                            )}
                            <div style={{
                              fontSize: '11px',
                              textAlign: 'center',
                              color: isSelected 
                                ? tenant.theme.colorPrimary 
                                : theme === "dark" ? "#d9d9d9" : "#262626",
                              fontWeight: isSelected ? 600 : 400,
                              lineHeight: '1.3',
                            }}>
                              {t(group?.translationCode || group?.name, {
                                defaultValue: group?.name,
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{
                      padding: '24px 16px',
                      textAlign: 'center',
                      color: theme === "dark" ? "#8c8c8c" : "#8c8c8c",
                      fontSize: '13px',
                    }}>
                      No modules found
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Collapsed Module Icons */}
        {collapsed && (
          <div style={{ padding: '8px 0' }}>
            {(menuList?.modules ?? []).map((group) => {
              if (group.code.includes("divider")) return null;
              const IconComponent = Icons[group.icon];
              const isSelected = selectedMenuGroup === group.code;

              return (
                <Tooltip
                  key={group.code}
                  title={t(group?.translationCode || group?.name, {
                    defaultValue: group?.name,
                  })}
                  placement="right"
                >
                  <div
                    onClick={() => handleMenuGroupSelect(group.code)}
                    style={{
                      padding: '12px 0',
                      textAlign: 'center',
                      cursor: 'pointer',
                      borderLeft: `3px solid ${isSelected ? tenant.theme.colorPrimary : 'transparent'}`,
                      backgroundColor: isSelected ? `${tenant.theme.colorPrimary}10` : 'transparent',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {IconComponent && (
                      <IconComponent 
                        style={{ 
                          fontSize: '20px',
                          color: isSelected ? tenant.theme.colorPrimary : theme === "dark" ? "#bfbfbf" : "#595959"
                        }} 
                      />
                    )}
                  </div>
                </Tooltip>
              );
            })}
          </div>
        )}

        {/* Developer Menu Section */}
        {!collapsed && isDeveloperMode && (
          <div style={{ 
            flex: 1, 
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Developer Tools Header */}
            <div style={{
              padding: '12px 16px',
              borderBottom: `1px solid ${theme === "dark" ? "#303030" : "#f0f0f0"}`,
              background: `linear-gradient(135deg, ${tenant.theme.colorPrimary}15, ${tenant.theme.colorPrimary}05)`,
            }}>
              <div style={{
                fontSize: '12px',
                fontWeight: 600,
                color: tenant.theme.colorPrimary,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <CodeOutlined />
                Developer Tools ({developerMenuGroups.reduce((acc, group) => acc + group.items.length, 0)})
              </div>
            </div>

            {/* Developer Menu Search */}
            <div style={{ padding: '12px' }}>
              <Input
                placeholder="Search developer tools..."
                prefix={<SearchOutlined style={{ color: theme === "dark" ? "#8c8c8c" : "#bfbfbf" }} />}
                value={menuSearchQuery}
                onChange={(e) => setMenuSearchQuery(e.target.value)}
                allowClear
                style={{
                  fontSize: '13px',
                  borderRadius: '6px',
                }}
              />
            </div>

            {/* Developer Menu Groups */}
            <div style={{ 
              flex: 1, 
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: '0 12px 12px 12px',
            }}>
              {developerMenuGroups.map((group) => {
                const GroupIcon = Icons[group.icon];
                const filteredItems = group.items.filter(item => {
                  if (!menuSearchQuery.trim()) return true;
                  const searchLower = menuSearchQuery.toLowerCase();
                  return item.label.toLowerCase().includes(searchLower) ||
                         item.description.toLowerCase().includes(searchLower) ||
                         group.category.toLowerCase().includes(searchLower);
                });

                if (filteredItems.length === 0 && menuSearchQuery.trim()) return null;

                return (
                  <div key={group.category} style={{ marginBottom: '16px' }}>
                    {/* Category Header */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      backgroundColor: theme === "dark" ? "#1f1f1f" : "#fafafa",
                      borderLeft: `3px solid ${group.color}`,
                    }}>
                      {GroupIcon && (
                        <GroupIcon style={{ fontSize: '14px', color: group.color }} />
                      )}
                      <span style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: theme === "dark" ? "#d9d9d9" : "#262626",
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}>
                        {group.category}
                      </span>
                      <span style={{
                        fontSize: '11px',
                        color: theme === "dark" ? "#8c8c8c" : "#8c8c8c",
                        marginLeft: 'auto',
                      }}>
                        {filteredItems.length}
                      </span>
                    </div>

                    {/* Category Items */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: '8px',
                    }}>
                      {filteredItems.map((item) => {
                        const IconComponent = Icons[item.icon];
                        const isActive = location.pathname === item.path;

                        return (
                          <Tooltip
                            key={item.key}
                            title={item.description}
                            placement="right"
                          >
                            <div
                              onClick={() => navigate(item.path)}
                              style={{
                                padding: '12px 8px',
                                borderRadius: '8px',
                                border: `1px solid ${isActive
                                  ? tenant.theme.colorPrimary
                                  : theme === "dark" ? "#303030" : "#f0f0f0"}`,
                                backgroundColor: isActive
                                  ? `${tenant.theme.colorPrimary}15`
                                  : theme === "dark" ? "#1f1f1f" : "#ffffff",
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '6px',
                                minHeight: '80px',
                              }}
                              onMouseEnter={(e) => {
                                if (!isActive) {
                                  e.currentTarget.style.borderColor = tenant.theme.colorPrimary + '80';
                                  e.currentTarget.style.backgroundColor = `${tenant.theme.colorPrimary}08`;
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                  e.currentTarget.style.boxShadow = theme === "dark"
                                    ? '0 4px 12px rgba(0,0,0,0.3)'
                                    : '0 4px 12px rgba(0,0,0,0.08)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isActive) {
                                  e.currentTarget.style.borderColor = theme === "dark" ? "#303030" : "#f0f0f0";
                                  e.currentTarget.style.backgroundColor = theme === "dark" ? "#1f1f1f" : "#ffffff";
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = 'none';
                                }
                              }}
                            >
                              {IconComponent && (
                                <IconComponent
                                  style={{
                                    fontSize: '20px',
                                    color: isActive ? tenant.theme.colorPrimary : theme === "dark" ? "#bfbfbf" : "#595959"
                                  }}
                                />
                              )}
                              <div style={{
                                fontSize: '11px',
                                textAlign: 'center',
                                color: isActive
                                  ? tenant.theme.colorPrimary
                                  : theme === "dark" ? "#d9d9d9" : "#262626",
                                fontWeight: isActive ? 600 : 500,
                                lineHeight: '1.3',
                              }}>
                                {item.label}
                              </div>
                            </div>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              
              {/* Empty State */}
              {developerMenuGroups.every(group => 
                group.items.filter(item => {
                  if (!menuSearchQuery.trim()) return true;
                  const searchLower = menuSearchQuery.toLowerCase();
                  return item.label.toLowerCase().includes(searchLower) ||
                         item.description.toLowerCase().includes(searchLower) ||
                         group.category.toLowerCase().includes(searchLower);
                }).length === 0
              ) && menuSearchQuery.trim() && (
                <div style={{
                  padding: '24px 16px',
                  textAlign: 'center',
                  color: theme === "dark" ? "#8c8c8c" : "#8c8c8c",
                  fontSize: '13px',
                }}>
                  No developer tools found
                </div>
              )}
            </div>
          </div>
        )}

        {/* Menu Items Section */}
        {!collapsed && !isDeveloperMode && (
          <div style={{ 
            flex: 1, 
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Menu Search */}
            <div style={{ padding: '12px' }}>
              <Input
                placeholder="Search menu items..."
                prefix={<SearchOutlined style={{ color: theme === "dark" ? "#8c8c8c" : "#bfbfbf" }} />}
                value={menuSearchQuery}
                onChange={(e) => setMenuSearchQuery(e.target.value)}
                allowClear
                style={{
                  fontSize: '13px',
                  borderRadius: '6px',
                }}
              />
            </div>

            <div style={{ 
              flex: 1, 
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: '0 0 8px 0',
            }}>
              {filteredMenuData.length > 0 ? (
                <Menu
                  mode="inline"
                  openKeys={menuSearchQuery ? filteredMenuData.map(item => item.code) : openKeys}
                  selectedKeys={[selectedCode]}
                  onOpenChange={(keys) => setOpenKeys(keys)}
                  style={{
                    border: 'none',
                    backgroundColor: 'transparent',
                  }}
                >
                  {filteredMenuData.map((item) => {
                    if (item.submenus) {
                      const visibleSubmenus = item.submenus || [];
                      
                      // Filter submenus if searching
                      const filteredSubmenus = menuSearchQuery 
                        ? visibleSubmenus.filter(subItem => {
                            const subLabel = t(subItem?.translationCode || subItem?.label, {
                              defaultValue: subItem?.label,
                            }).toLowerCase();
                            return subLabel.includes(menuSearchQuery.toLowerCase());
                          })
                        : visibleSubmenus;

                      if (menuSearchQuery && filteredSubmenus.length === 0) return null;

                      return (
                        <SubMenu
                          key={item.code}
                          icon={(() => {
                            const IconComponent = Icons[item.icon];
                            return IconComponent ? (
                              <IconComponent style={{ fontSize: '16px' }} />
                            ) : null;
                          })()}
                          title={
                            <span style={{ 
                              fontSize: '14px',
                              fontWeight: 500,
                            }}>
                              {t(item?.translationCode || item?.label, {
                                defaultValue: item?.label,
                              })}
                            </span>
                          }
                          style={{
                            margin: '4px 8px',
                            borderRadius: '6px',
                          }}
                        >
                          {filteredSubmenus.map((subItem) => (
                            <Menu.Item
                              key={subItem.code}
                              icon={(() => {
                                const IconComponent = Icons[subItem.icon];
                                return IconComponent ? (
                                  <IconComponent style={{ fontSize: '14px' }} />
                                ) : null;
                              })()}
                              onClick={() =>
                                handleMenuItemClick(
                                  subItem.path,
                                  subItem.code,
                                  subItem
                                )
                              }
                              style={{
                                margin: '2px 4px',
                                borderRadius: '6px',
                                paddingLeft: '48px',
                                height: '36px',
                                lineHeight: '36px',
                                fontSize: '13px',
                              }}
                            >
                              {t(subItem?.translationCode || subItem?.label, {
                                defaultValue: subItem?.label,
                              })}
                            </Menu.Item>
                          ))}
                        </SubMenu>
                      );
                    }

                    return (
                      <Menu.Item
                        key={item.code}
                        icon={(() => {
                          const IconComponent = Icons[item.icon];
                          return IconComponent ? (
                            <IconComponent style={{ fontSize: '16px' }} />
                          ) : null;
                        })()}
                        onClick={() => handleMenuItemClick(item.path, item.code, item)}
                        style={{
                          margin: '4px 8px',
                          borderRadius: '6px',
                          height: '40px',
                          lineHeight: '40px',
                          fontSize: '14px',
                          fontWeight: 500,
                        }}
                      >
                        {t(item?.translationCode || item?.label, {
                          defaultValue: item?.label,
                        })}
                      </Menu.Item>
                    );
                  })}
                </Menu>
              ) : (
                <div style={{
                  padding: '24px 16px',
                  textAlign: 'center',
                  color: theme === "dark" ? "#8c8c8c" : "#8c8c8c",
                  fontSize: '13px',
                }}>
                  {menuSearchQuery ? 'No menu items found' : 'No items available'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* DynamicDrawer Component */}
      <DynamicDrawer
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        formFieldsColumn={drawerConfig.formFieldsColumn}
        configCmd={drawerConfig.configCmd}
        configParams={drawerConfig.configParams}
        width={drawerConfig.width}
        placement={drawerConfig.placement}
      />
    </>
  );
};

export default MainLayout;
